import React from 'react';

interface State {
  error: Error | null;
  componentStack: string;
}

/**
 * There was none of this until 2026-09-14. React unmounts the whole tree on an uncaught render
 * error by default, so a crash anywhere — a bad response shape, a null a component didn't expect —
 * read as "the page goes blank," with nothing to diagnose it from. This turns that into a visible
 * message with a reload button, which is strictly better even once the specific crash that prompted
 * it is found and fixed: it is the only thing that makes the *next* one diagnosable at all.
 *
 * The first version showed only `error.message`, and on a minified build that was "n is not a
 * function" — true, and useless. This shows the full stack and the component stack too (which name
 * is not a function still needs both to be useful), and the build this ships with turns minification
 * off (vite.config.ts) so the names in that stack are the real ones, not single letters.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null, componentStack: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? '' });
    // eslint-disable-next-line no-console
    console.error('Scribe crashed:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { error, componentStack } = this.state;
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6 text-slate-100">
        <div className="glass-panel max-w-2xl space-y-3 rounded-2xl border-rose-800 p-8">
          <h1 className="text-lg font-bold text-rose-300">Something broke</h1>
          <p className="text-sm text-slate-400">
            This screen crashed instead of quietly going blank. Reloading returns you to the worklist;
            if it keeps happening on the same study, everything below matters for fixing it.
          </p>
          <pre className="max-h-64 overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-rose-300">
            {error.message}
            {error.stack && `\n\n${error.stack}`}
            {componentStack && `\n--- component stack ---${componentStack}`}
          </pre>
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500"
            >
              Reload
            </button>
            <button
              onClick={() => {
                const text = `${error.message}\n\n${error.stack ?? ''}\n--- component stack ---${componentStack}`;
                void navigator.clipboard?.writeText(text);
              }}
              className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-300 hover:border-slate-500"
            >
              Copy details
            </button>
          </div>
        </div>
      </div>
    );
  }
}
